import { InvestmentFormValues } from "./types";

type MockVariant = (currencyOptions: any[]) => Partial<InvestmentFormValues>;

const pickCurrency = (currencyOptions: any[], exclude?: string[]): string => {
  const filtered = exclude?.length
    ? currencyOptions.filter((o) => !exclude.includes(o.label))
    : currencyOptions;
  return filtered[0]?.value ?? "";
};

const pickCurrencyAt = (currencyOptions: any[], index: number, exclude?: string[]): string => {
  const filtered = exclude?.length
    ? currencyOptions.filter((o) => !exclude.includes(o.label))
    : currencyOptions;
  return filtered[index % Math.max(filtered.length, 1)]?.value ?? "";
};

export const mockVariantsBySlug: Record<string, MockVariant[]> = {
  stocks: [
    (c) => ({
      name: "Apple Inc.",
      quantity: "50",
      purchaseRate: "178.50",
      primaryValue: "8925",
      currencyUuid: pickCurrencyAt(c, 0, ["PLN"]),
      additionalInfo: "Akcje zakupione przez brokera XTB w grudniu 2023.",
    }),
    (c) => ({
      name: "Tesla Inc.",
      quantity: "30",
      purchaseRate: "245.80",
      primaryValue: "7374",
      currencyUuid: pickCurrencyAt(c, 1, ["PLN"]),
      additionalInfo: "Zakup na platformie Interactive Brokers, styczeń 2024.",
    }),
    (c) => ({
      name: "Microsoft Corp.",
      quantity: "15",
      purchaseRate: "415.20",
      primaryValue: "6228",
      currencyUuid: pickCurrencyAt(c, 0, ["PLN"]),
      additionalInfo: "Długoterminowa inwestycja, planowany horyzont 10 lat.",
    }),
  ],

  shares: [
    (c) => ({
      name: "PKN Orlen S.A.",
      quantity: "120",
      purchaseRate: "62.40",
      primaryValue: "7488",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Udziały nabyte w ramach oferty publicznej.",
    }),
    (c) => ({
      name: "CD Projekt S.A.",
      quantity: "80",
      purchaseRate: "89.90",
      primaryValue: "7192",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Zakup po premierze Cyberpunk 2077 Phantom Liberty.",
    }),
    (c) => ({
      name: "Allegro.eu S.A.",
      quantity: "200",
      purchaseRate: "34.60",
      primaryValue: "6920",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Pozycja otwarta w Q3 2023, planowany dodatkowy zakup.",
    }),
  ],

  real_estate: [
    (c) => ({
      propertyType: "apartment",
      city: "Warszawa",
      address: "ul. Mokotowska 18/4",
      primaryValue: "720000",
      currencyUuid: pickCurrency(c),
      area: "68.5",
      additionalInfo: "Mieszkanie 3-pokojowe w centrum Warszawy, po generalnym remoncie.",
    }),
    (c) => ({
      propertyType: "house",
      city: "Kraków",
      address: "ul. Wierzbowa 5",
      primaryValue: "1150000",
      currencyUuid: pickCurrency(c),
      area: "185",
      additionalInfo: "Dom jednorodzinny z ogrodem, garaż 2-stanowiskowy.",
    }),
    (c) => ({
      propertyType: "land",
      city: "Gdańsk",
      address: "ul. Słoneczna 22",
      primaryValue: "420000",
      currencyUuid: pickCurrency(c),
      area: "1200",
      additionalInfo: "Działka budowlana z pozwoleniem na budowę, media na działce.",
    }),
  ],

  crypto: [
    (c) => ({
      name: "Bitcoin",
      quantity: "0.35",
      primaryValue: "220000",
      purchaseDate: "2023-04",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Zakupiony na giełdzie Binance.",
    }),
    (c) => ({
      name: "Ethereum",
      quantity: "4.5",
      primaryValue: "48600",
      purchaseDate: "2023-09",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Stake'owany na platformie Lido, APY ok. 4%.",
    }),
    (c) => ({
      name: "Solana",
      quantity: "120",
      primaryValue: "21600",
      purchaseDate: "2024-01",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Zakupiona na giełdzie Kraken, przechowywana w portfelu Phantom.",
    }),
  ],

  precious_stones: [
    (c) => ({
      name: "Diament brylantowy 1.2 ct",
      primaryValue: "28000",
      currencyUuid: pickCurrency(c),
      weight: "1.2",
      cut: "Brylantowy",
      color: "D",
      clarity: "VS1",
      additionalInfo: "Certyfikat GIA nr 2201546789.",
    }),
    (c) => ({
      name: "Szmaragd kolumbijski 2.4 ct",
      primaryValue: "42000",
      currencyUuid: pickCurrency(c),
      weight: "2.4",
      cut: "Schodkowy",
      color: "Zielony intensywny",
      clarity: "Minor inclusions",
      additionalInfo: "Certyfikat Gübelin, pochodzenie: Muzo, Kolumbia.",
    }),
    (c) => ({
      name: "Rubin birmański 0.9 ct",
      primaryValue: "31000",
      currencyUuid: pickCurrency(c),
      weight: "0.9",
      cut: "Owalny",
      color: "Pigeon Blood",
      clarity: "Eye clean",
      additionalInfo: "Certyfikat AGL, bez oznak ogrzewania.",
    }),
  ],

  precious_metals: [
    (c) => ({
      propertyType: "gold",
      primaryValue: "14500",
      currencyUuid: pickCurrency(c),
      weight: "220",
      finess: "999.9",
      additionalInfo: "Sztabki złota przechowywane w sejfie bankowym.",
    }),
    (c) => ({
      propertyType: "silver",
      primaryValue: "3800",
      currencyUuid: pickCurrency(c),
      weight: "3000",
      finess: "999",
      additionalInfo: "Monety bulionowe Filharmonik wiedeński, rocznik 2023.",
    }),
    (c) => ({
      propertyType: "gold",
      primaryValue: "8200",
      currencyUuid: pickCurrency(c),
      weight: "125",
      finess: "585",
      additionalInfo: "Biżuteria złota — bransoletki i łańcuszki, wycena jubilerska.",
    }),
  ],

  treasury_bonds: [
    (c) => ({
      quantity: "100",
      primaryValue: "10000",
      currencyUuid: pickCurrency(c),
      interestRate: "6.75",
      interestType: "periodically_fixed",
      additionalInfo: "Obligacje skarbowe serii EDO nabyte w ofercie detalicznej.",
    }),
    (c) => ({
      quantity: "50",
      primaryValue: "5000",
      currencyUuid: pickCurrency(c),
      interestRate: "5.50",
      interestType: "fixed",
      additionalInfo: "Obligacje 4-letnie COI, zakup w ramach IKE.",
    }),
    (c) => ({
      quantity: "200",
      primaryValue: "20000",
      currencyUuid: pickCurrency(c),
      interestRate: "4.00",
      interestType: "variable",
      additionalInfo: "Obligacje ROR oparte o stopę referencyjną NBP.",
    }),
  ],

  artwork: [
    (c) => ({
      artworkType: "Obraz olejny",
      primaryValue: "48000",
      currencyUuid: pickCurrency(c),
      unknownAuthor: false,
      author: "Stanisław Wyspiański",
      name: "Pejzaż tatrzański",
      eraAge: "XIX/XX w.",
      additionalInfo: "Praca pochodzi z prywatnej kolekcji. Certyfikat autentyczności.",
    }),
    (c) => ({
      artworkType: "Rzeźba",
      primaryValue: "75000",
      currencyUuid: pickCurrency(c),
      unknownAuthor: false,
      author: "Magdalena Abakanowicz",
      name: "Figura siedząca",
      eraAge: "XX w.",
      additionalInfo: "Rzeźba z brązu, casting 3/8, wystawa retrospektywna 2001.",
    }),
    (c) => ({
      artworkType: "Fotografia artystyczna",
      primaryValue: "12000",
      currencyUuid: pickCurrency(c),
      unknownAuthor: true,
      author: "Autor nieznany",
      name: "Bez tytułu #14",
      eraAge: "XXI w.",
      additionalInfo: "Odbitka srebrowa, edycja limitowana 5/20, sygnowana przez galerię.",
    }),
  ],

  investment_policy: [
    (c) => ({
      name: "Polisa inwestycyjna Aegon Invest",
      currencyUuid: pickCurrency(c),
      contractDate: "2022-03-15",
      premiumPayment: "yearly",
      durationDate: "specific_date",
      durationDateValue: "2032-03-15",
      additionalInfo: "Polisa z funduszem kapitałowym, wariant agresywny.",
    }),
    (c) => ({
      name: "Polisa na życie z UFK – Generali",
      currencyUuid: pickCurrency(c),
      contractDate: "2020-09-01",
      premiumPayment: "monthly",
      durationDate: "indefinite",
      additionalInfo: "Umowa bezterminowa, składka 500 zł miesięcznie.",
    }),
    (c) => ({
      name: "Unit-linked PZU Invest 2025",
      currencyUuid: pickCurrency(c),
      contractDate: "2023-01-10",
      premiumPayment: "quarterly",
      durationDate: "specific_date",
      durationDateValue: "2030-01-10",
      additionalInfo: "Fundusze zrównoważone, alokacja 60/40 akcje/obligacje.",
    }),
  ],

  loans: [
    (c) => ({
      loanType: "borrowed",
      entity: "mBank S.A.",
      primaryValue: "250000",
      currencyUuid: pickCurrency(c),
      interestRate: "8.5",
      interestRateType: "variable",
      interestPayment: "monthly",
      contractDate: "2023-06-01",
      durationDate: "specific_date",
      durationDateValue: "2043-06-01",
      autoRenewal: "false",
      additionalInfo: "Kredyt hipoteczny na zakup nieruchomości w Krakowie.",
    }),
    (c) => ({
      loanType: "lent",
      entity: "Jan Kowalski",
      primaryValue: "30000",
      currencyUuid: pickCurrency(c),
      interestRate: "5.0",
      interestRateType: "fixed",
      interestPayment: "at_end_of_year",
      contractDate: "2024-02-15",
      durationDate: "specific_date",
      durationDateValue: "2026-02-15",
      autoRenewal: "true",
      additionalInfo: "Pożyczka prywatna z umową notarialną.",
    }),
    (c) => ({
      loanType: "borrowed",
      entity: "PKO Bank Polski S.A.",
      primaryValue: "45000",
      currencyUuid: pickCurrency(c),
      interestRate: "9.2",
      interestRateType: "variable",
      interestPayment: "monthly",
      contractDate: "2022-11-20",
      durationDate: "specific_date",
      durationDateValue: "2028-11-20",
      autoRenewal: "false",
      additionalInfo: "Kredyt gotówkowy na remont mieszkania.",
    }),
  ],

  currencies: [
    (c) => ({
      currencyUuid: pickCurrencyAt(c, 0, ["PLN"]),
      primaryValue: "10000",
      purchaseRate: "4.23",
      purchaseDate: "2024-01-15",
      additionalInfo: "Waluta nabyta w kantorze internetowym Conotoxia.",
    }),
    (c) => ({
      currencyUuid: pickCurrencyAt(c, 1, ["PLN"]),
      primaryValue: "5000",
      purchaseRate: "1.09",
      purchaseDate: "2024-03-08",
      additionalInfo: "Zakup w oddziale banku przed wyjazdem służbowym.",
    }),
    (c) => ({
      currencyUuid: pickCurrencyAt(c, 2, ["PLN"]),
      primaryValue: "8000",
      purchaseRate: "4.55",
      purchaseDate: "2023-12-20",
      additionalInfo: "Wymiana walutowa przez aplikację Revolut.",
    }),
  ],

  other_investments: [
    (c) => ({
      name: "Kolekcja monet kolekcjonerskich",
      primaryValue: "9200",
      currencyUuid: pickCurrency(c),
      additionalInfo: "Kolekcja 45 monet z lat 1960–2000, w tym numizmaty NBP.",
    }),
    (c) => ({
      name: "Wino inwestycyjne Château Pétrus 2015",
      primaryValue: "18500",
      currencyUuid: pickCurrencyAt(c, 0, ["PLN"]),
      additionalInfo: "12 butelek, przechowywane w kontrolowanej temperaturze w magazynie winiarskim.",
    }),
    (c) => ({
      name: "Whisky kolekcjonerska – Macallan 30Y",
      primaryValue: "22000",
      currencyUuid: pickCurrencyAt(c, 0, ["PLN"]),
      additionalInfo: "3 butelki limitowanej edycji, oryginalne pudełka, certyfikaty.",
    }),
  ],
};

// Licznik kliknięć per slug — resetuje się przy zamknięciu formularza (per instancja modułu)
const fillCounters: Record<string, number> = {};

export const getMockData = (
  slug: string,
  currencyOptions: any[],
): Partial<InvestmentFormValues> => {
  const variants = mockVariantsBySlug[slug] ?? mockVariantsBySlug["other_investments"];
  const idx = fillCounters[slug] ?? 0;
  fillCounters[slug] = (idx + 1) % variants.length;
  return variants[idx](currencyOptions);
};
